/**
 * @author NikolaV
 */
import { Ajax } from '@shared/Ajax';
import { fitExtent, latLonToEN } from '@openlayers/ol-utils';
import { Notify } from '@shared/ui-native/Notify';
import { MapOverlays } from '@openlayers/ol-overlays';
import { store } from '../store/rootStore';
import * as React from 'react';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import OlPoint from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import OlMap from 'ol/Map';
import OlLayer from 'ol/layer/Layer';
import * as olStyle from 'ol/style';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { TruckTripInfoCard, TruckTripInfoDetails } from '../ui/shared/TruckTripInfoCard';
import imgStartMarker from '../../../../assets/images/startMarker.png';
import imgEndMarker from '../../../../assets/images/endMarker.png';
import ReactDOMServer from 'react-dom/server';
import { TripsStore } from '@pages/trucks/store/TripsStore';

interface ITrackPoint {
    lat: number;
    lon: number;
    t: any;
    tripId: number;
}

interface ITracksResponse {
    tracks: {
        [key: string]: ITrackPoint[];
    };
}

let mapInstance: OlMap = null;
let initialLoad = true;

export const source: VectorSource = new VectorSource({
    features: [],
    useSpatialIndex: true,
});

const layer: OlLayer = new VectorLayer({
    source,
    updateWhileAnimating: false,
    updateWhileInteracting: true,
});

const styles = {
    tailGreen: new olStyle.Style({
        stroke: new olStyle.Stroke({
            color: '#15d315',
            width: 3,
        }),
    }),
    tailRed: new olStyle.Style({
        stroke: new olStyle.Stroke({
            color: '#d70808',
            width: 3,
        }),
    }),
    startMarker: new olStyle.Style({
        image: new olStyle.Icon({
            src: imgStartMarker,
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            anchor: [0.5, 1],
        }),
    }),
    endMarker: new olStyle.Style({
        image: new olStyle.Icon({
            src: imgEndMarker,
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            anchor: [0.5, 1],
        }),
    }),
    hitArea: new olStyle.Style({
        stroke: new olStyle.Stroke({
            color: [0, 0, 0, 0.005],
            width: 30,
        }),
    }),
    hitAreaCircle: new olStyle.Style({
        image: new olStyle.Circle({
            radius: 50,
            fill: new olStyle.Fill({
                color: [0, 0, 0, 0.005],
            }),
        }),
    }),
};

export function drawTripsTracks(trips: ITruckTrip[], map: OlMap, wholeTableSelected?: boolean) {
    mapInstance = map;

    if (initialLoad) {
        initialLoad = false;
        layer.setZIndex(999);
        mapInstance.addLayer(layer);
    }

    MapOverlays.hideDialog(mapInstance);
    store.tripsTableScrollTop = document.querySelector('.p-datatable-scrollable-body')?.scrollTop ?? 0;

    const apiFilter = store.tripsFilter.toApiFilter();

    const convertArraysToStringsExcept = (obj, excludeKeys) => {
        for (const key in obj) {
            if (Array.isArray(obj[key]) && !excludeKeys.includes(key)) {
                obj[key] = obj[key].join(',');
            }
        }
        return obj;
    };
    const excludeKeys = ['includeEmptyTrips', 'distanceFrom', 'distanceTo'];
    const convertedData = convertArraysToStringsExcept(apiFilter, excludeKeys);

    return Ajax({
        url: 'vehicle_trip_tracks.json',
        data: {
            tripIds: trips.map(t => t.id),
            allSelected: wholeTableSelected,
            ...convertedData,
        },
    })
        .fail(() => Notify.error('Error while fetching track'))
        .then((resp: ITracksResponse) => {
            if (Object.keys(resp.tracks).length == 0) {
                Notify.warn('No track found for this task');
                return $.Deferred().reject();
            }

            source.clear();

            Object.entries(resp.tracks).forEach(([tripId, track]) => {
                let lastPosition = track[0];
                const trip = trips.find(t => t.id === parseInt(tripId));

                // popup html
                const Component = <TruckTripInfoCard trips={[trip]} />;
                ReactUtils.renderToString(Component, 'truck_trip_' + tripId, (html: string) => {
                    const isReturnTrip = !trip.material || !trip.volume || trip.volume == 0;
                    const tripStyle = isReturnTrip ? styles.tailRed : styles.tailGreen;
                    track.forEach((t, i) => {
                        const point = latLonToEN(t.lat, t.lon);

                        // start marker
                        if (i == 0) {
                            const startMarkerFeature = new Feature({
                                geometry: new OlPoint(point),
                            });
                            startMarkerFeature.setStyle([styles.startMarker]);
                            startMarkerFeature.set('dialogContent', html);
                            startMarkerFeature.set('tripId', tripId);
                            source.addFeature(startMarkerFeature);
                        }

                        // end marker
                        else if (i == track.length - 1) {
                            const endMarkerFeature = new Feature({
                                geometry: new OlPoint(point),
                            });
                            endMarkerFeature.setStyle([styles.endMarker]);
                            endMarkerFeature.set('dialogContent', html);
                            endMarkerFeature.set('tripId', tripId);
                            source.addFeature(endMarkerFeature);
                        }

                        if (i > 0) {
                            const segmentFeature = new Feature({
                                geometry: new LineString([point, latLonToEN(lastPosition.lat, lastPosition.lon)]),
                            });

                            segmentFeature.setStyle([styles.hitArea, tripStyle]);
                            segmentFeature.set('type', 'trip_segment');
                            segmentFeature.set('dialogContent', html);
                            segmentFeature.set('tripId', tripId);
                            source.addFeature(segmentFeature);
                        }

                        lastPosition = track[i];
                    });

                    // fit visible
                    const sidebar = $('.js-sidebar');
                    let sidebarWidth = 100;
                    if (sidebar.length) {
                        sidebarWidth = parseInt(sidebar.css('left'), 10) < 0 ? 20 : sidebar.width() + 30;
                    }
                    const padding: [number, number, number, number] = [240, 80, 50, sidebarWidth];
                    fitExtent(mapInstance, source.getExtent(), 250, padding);

                    store.isMapCleared = false;
                });
            });

            return resp.tracks;
        });
}

export async function onClick(feature: Feature, e, n) {
    const dialogContent = feature.get('dialogContent');
    const tripId = feature.get('tripId');

    const tripsStore = new TripsStore();

    if (dialogContent === '<div></div>' && tripId) {
        try {
            const tripPromise = await tripsStore.fetchSingleTrip(tripId).fail(Notify.error);
            const trip = tripPromise?.trip;
            if (!trip) return;

            const Content = ReactDOMServer.renderToString(TruckTripInfoDetails(trip));

            feature.set('dialogContent', Content);
            MapOverlays.showDialog(mapInstance, Content, e, n, feature);
        } catch (error) {
            console.error('An unexpected error occurred:', error);
        }
    } else {
        MapOverlays.showDialog(mapInstance, dialogContent, e, n, feature);
    }
}
